/* eslint-disable max-len */
import { cacheDecorator } from '@ds/swymkit/tools'
import { defineStore } from 'pinia'

import * as service from '~/entities/container/community/api/communities'
import { getUnfollowedCommunities } from '~/entities/container/community/api/communities'
import type { CreateCommunityData, ICommunity, ICommunityContentTypeEnabled, ICommunityContentTypeMinACL, ICommunityLiteMarksResponse } from '~/entities/container/community/interfaces/community'
import { ECommunityRole } from '~/entities/container/community/interfaces/community'

import { ContentTypeName, getContentTypeNotProcessed } from '~/features-shared/content/composables/content-types'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { useDerivedTypesStore } from '~/features-shared/content-derived-type/stores/derived-types'
import { useCommunityFacetStore } from '~/features-shared/facet/stores/facets'
import { useFavoritesStore } from '~/features-shared/favorites/stores/favorites'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
// import { getSections } from '~/features-shared/sections/api/sections'
// import { useSectionsStore } from '~/features-shared/sections/store/sections'

import type { SwymResponse } from '~/interfaces/services-dto'

type CONTENT_TYPES_WITH_ACL = ContentTypeName.POST | ContentTypeName.MEDIA | ContentTypeName.IDEA | ContentTypeName.QUESTION | ContentTypeName.WIKI | ContentTypeName.SURVEY | ContentTypeName.WEDO
type MainContentTypes = ContentTypeName.POST | ContentTypeName.MEDIA | ContentTypeName.IDEA | ContentTypeName.QUESTION | ContentTypeName.WIKI | ContentTypeName.SURVEY | ContentTypeName.WEDO
const TYPE_ACL_ATTRIBUTE_MAPPING: Record<MainContentTypes, keyof ICommunityContentTypeMinACL> = {
  [ContentTypeName.POST]: 'post_min_acl',
  [ContentTypeName.MEDIA]: 'media_min_acl',
  [ContentTypeName.IDEA]: 'ideation_min_acl',
  [ContentTypeName.QUESTION]: 'qna_min_acl',
  [ContentTypeName.WIKI]: 'wiki_min_acl',
  [ContentTypeName.SURVEY]: 'survey_min_acl',
  [ContentTypeName.WEDO]: 'wedo_min_acl',
}
const TYPE_ENABLED_ATTRIBUTE_MAPPING: Record<MainContentTypes, keyof ICommunityContentTypeEnabled> = {
  [ContentTypeName.POST]: 'post_is_enabled',
  [ContentTypeName.MEDIA]: 'media_is_enabled',
  [ContentTypeName.IDEA]: 'ideation_is_enabled',
  [ContentTypeName.QUESTION]: 'qna_is_enabled',
  [ContentTypeName.WIKI]: 'wiki_is_enabled',
  [ContentTypeName.SURVEY]: 'survey_is_enabled',
  [ContentTypeName.WEDO]: 'wedo_is_enabled',
}

export function isCommunityUnread(el: ICommunity) {
  return el.last_message
    ? el.mark?.message_id !== el.last_message?.message_id
    : false
}

// eslint-disable-next-line max-statements
export const useCommunitiesStore = defineStore('communities', () => {
  let marksFetched = false

  const storeFavorites = useFavoritesStore()
  const storeDerivedTypes = useDerivedTypesStore()
  const storeFacets = useCommunityFacetStore()
  // TODO: Uncomment in 25xFD03
  // const storeSections = useSectionsStore()

  // #region STATES
  const threadsId = reactive<string[]>([])
  const threads = reactive<Map<string, ICommunity>>(new Map())
  const globalMarks = reactive<Map<string, string>>(new Map())
  // #endregion STATES

  // #region METHODS
  const getSubjectUriFromId = (id: string) => threadsId.find(el => el.endsWith(`:${id}`)) || id
  const getCommunityByUri = (subject_uri?: SUBJECT_6W_URI): ICommunity | undefined => (
    subject_uri ? threads.get(subject_uri) : undefined
  )
  const getCommunityById = (id: string) => getCommunityByUri(getSubjectUriFromId(id))
  const getCommunityUserAccess = (communityUri?: SUBJECT_6W_URI): ECommunityRole => (
    getCommunityByUri(communityUri || '')?.user_access || Number.NaN
  )
  const isCommunityUnread = (el: ICommunity, context?: 'favorite_communities' | 'communities') => {
    const globalMarkTime = context
      ? Number.parseInt(globalMarks.get(context)
        ?.match(/(.*-.*)-.*/)?.[0]?.replace('-', '') || '0', 16)
      : 0
    const messageTime = Number.parseInt(el.last_message?.message_id?.match(/(.*-.*)-.*/)?.[0]?.replace('-', '') || '0', 16)

    if (messageTime < globalMarkTime)
      return false
    return (el.last_message ? el.mark?.message_id !== el.last_message?.message_id : false)
  }

  /**
   * Is current user has the expected community user access.
   * @param options - Options
   * @param options.communityUri - Community subject URI.
   * @param options.targetUserAccess - Target community user access to compare with the current user access.
   * @param options.or - undefined: Compare the exact user access.
   *                     'higher': Compare if has exact or has higher user access.
   *                     'lesser' Compare if has exact or has lesser user access.
   * @private
   */
  function isCommunityUserAccess({
    communityUri = '',
    targetUserAccess,
    or,
  }: {
    communityUri?: SUBJECT_6W_URI
    targetUserAccess: ECommunityRole
    or?: 'higher' | 'lesser'
  }): boolean {
    const communityUserAccess = getCommunityUserAccess(communityUri)
    let is = false

    if (or === 'higher')
      is = communityUserAccess >= targetUserAccess
    else if (or === 'lesser')
      is = communityUserAccess <= targetUserAccess
    else
      is = communityUserAccess === targetUserAccess

    return is
  }

  /**
   * Has current user content creation access
   * @param communityUri - Community subject URI.
   * @param targetContent - Target content for which creation access to be checked
   */
  function hasContentCreationAccess(communityUri: SUBJECT_6W_URI, targetContent: CONTENT_TYPES_WITH_ACL): boolean {
    const aclAttribute = TYPE_ACL_ATTRIBUTE_MAPPING[targetContent]
    const isEnabledAttribute = TYPE_ENABLED_ATTRIBUTE_MAPPING[targetContent]
    const isContentEnabled = isEnabledAttribute && getCommunityByUri(communityUri || '')?.[isEnabledAttribute] as boolean
    const contentAccessRole = aclAttribute && getCommunityByUri(communityUri || '')?.[aclAttribute] as ECommunityRole
    return isContentEnabled && (getCommunityUserAccess(communityUri) >= contentAccessRole)
  }

  /**
   * Is current user has the SuperUser community user access.
   * @param communityUri - Community subject URI.
   * @param or - undefined: Compare the exact user access.
   *             'higher': Compare if has exact or has higher user access.
   *             'lesser' Compare if has exact or has lesser user access.
   */
  function isCommunitySuperUser(communityUri?: SUBJECT_6W_URI, or?: 'higher' | 'lesser'): boolean {
    return isCommunityUserAccess({ communityUri, targetUserAccess: ECommunityRole.SUPERUSER, or })
  }

  /**
   * Is current user has the Owner community user access.
   * @param communityUri - Community subject URI.
   * @param or - undefined: Compare the exact user access.
   *             'higher': Compare if has exact or has higher user access.
   *             'lesser' Compare if has exact or has lesser user access.
   */
  function isCommunityOwner(communityUri?: SUBJECT_6W_URI, or?: 'higher' | 'lesser'): boolean {
    return isCommunityUserAccess({ communityUri, targetUserAccess: ECommunityRole.OWNER, or })
  }

  /**
   * Is current user has the Author community user access.
   * @param communityUri - Community subject URI.
   * @param or - undefined: Compare the exact user access.
   *             'higher': Compare if has exact or has higher user access.
   *             'lesser' Compare if has exact or has lesser user access.
   */
  function isCommunityAuthor(communityUri?: SUBJECT_6W_URI, or?: 'higher' | 'lesser'): boolean {
    return isCommunityUserAccess({ communityUri, targetUserAccess: ECommunityRole.AUTHOR, or })
  }

  /**
   * Is current user has the Contributor community user access.
   * @param communityUri - Community subject URI.
   * @param or - undefined: Compare the exact user access.
   *             'higher': Compare if has exact or has higher user access.
   *             'lesser' Compare if has exact or has lesser user access.
   */
  function isCommunityContributor(communityUri?: SUBJECT_6W_URI, or?: 'higher' | 'lesser'): boolean {
    return isCommunityUserAccess({ communityUri, targetUserAccess: ECommunityRole.CONTRIBUTOR, or })
  }

  /**
   * Is the community a Business Experience community.
   * @param communityUri - Community subject URI.
   */
  function isBusinessExperienceCommunity(communityUri: SUBJECT_6W_URI): boolean {
    return getCommunityByUri(communityUri)?.community_be ?? false
  }

  /**
   * Is current user allowed to create a type of content
   * @param communityUri - Community subject URI.
   * @param contentTypeName - The content type name
   * @returns true if contributor is allowed
   */
  function isContributorAllowedToCreateContent(
    communityUri: SUBJECT_6W_URI,
    contentTypeName: ContentTypeName,
  ): boolean {
    const community = getCommunityByUri(communityUri || '')
    if (!community)
      return false

    const contentTypeTemplate = getContentTypeNotProcessed(contentTypeName)
    const min_acl_token = community[(contentTypeTemplate?.backend?.min_acl_token || '') as keyof ICommunity] as string | undefined
    return !!(min_acl_token && (Number.parseInt(min_acl_token) < 2))
  }
  // #endregion METHODS

  // #region GETTERS
  const threadsVisible = computed(() => [...threads.values()].filter(el => !el.is_hidden))
  const countUnread = computed(() => {
    const favorites = storeFavorites.favorites.get('community')
    return threadsVisible.value
      .filter(community =>
        isCommunityUnread(community, favorites
          ?.has(community.subject_uri)
          ? 'favorite_communities'
          : 'communities'))
      .length
  })
  // #endregion GETTERS

  // #region ACTIONS
  const doFilter = async (filter: string) => {
    if (filter) {
      return [...threads.values()].filter(el => el.title.toLowerCase()
        .includes(filter.toLowerCase()))
        .map(el => el.id)
    }
    else { return undefined }
  }
  const fetchList = async () => {
    const communitiesResult = (await service.getCommunities()).result
    // TODO: code will uncomment in 25xFD03
    // const sections = (await getSections('community'))
    // storeSections.sections.set('community', new Map(sections.map(item => [item.id, item])))

    communitiesResult.forEach((i) => {
      const community = threads.get(i.subject_uri) || {}
      threads.set(i.subject_uri, Object.assign(community, i))
    })
    threadsId.push(...communitiesResult.map(i => i.subject_uri))

    try {
      if (marksFetched)
        return

      const communitiesMarks = (await service.getCommunitiesMarks()).result.communities

      // Add the marks
      communitiesMarks.forEach((i) => {
        const community = threads.get(i.subject_uri)
        if (community) {
          community.mark = i.mark
          community.last_message = i.last_message
          threads.set(i.subject_uri, community)
        }
      })
      marksFetched = true
    }
    catch { }
  }
  const fetchCommunity = async (subject_uri: SUBJECT_6W_URI) => {
    const community = (await service.getCommunity(subject_uri)).result
    const c = getCommunityByUri(subject_uri) || {}
    if (community.subject_uri && community.community_type !== 'dm') {
      threads.set(community.subject_uri, Object.assign(c, community))
      if (!threadsId.includes(community.subject_uri))
        threadsId.push(community.subject_uri)

      // get the facets
      await storeFacets.fetchOrGetCommunityFacets(community.subject_uri)
      // Asynchronously get all the derived types. TODO: Check if we need to add await
      if (community.community_be)
        storeDerivedTypes.fetchDerivedTypesForCommunity(community.subject_uri)

      return community
    }
  }

  /**
   * This methods fetches the community along with tags. We need tags for the tagger
   * of settings page
   * @param subject_uri The subject uri
   */
  const setCommunityExplicitTags = async (subject_uri: SUBJECT_6W_URI) => {
    const community = (await service.getCommunityWithTags(subject_uri)).result
    const c = getCommunityByUri(subject_uri) || {}
    if (community.subject_uri && community.community_type !== 'dm') {
      threads.set(community.subject_uri, Object.assign(c, community))
      if (!threadsId.includes(community.subject_uri))
        threadsId.push(community.subject_uri)
    }
  }

  // Fetches community details if it exists in store, otherwise makes an API call to get community details.
  const getOrFetchCommunity = async (id: string) => {
    let community = getCommunityById(id)
    // If community details exist in store, return it.
    if (community && community.user_access) // access is present only in unitary community fetch, so use it for check
      return community
    // API call to get community details and unfollowed communities list.
    const [newCommunity, unfollowedList] = await Promise.all([fetchCommunity(id), getUnfollowedCommunities()])
    if (newCommunity)
      newCommunity.is_hidden = false // Mark new community as followed

    // Search for new community in unfollowed communities list
    for (const unfollowedCommunity of unfollowedList.result) {
      if (newCommunity && unfollowedCommunity.id === newCommunity?.id)
        newCommunity.is_hidden = true // Mark new community as unfollowed when found in unfollowed list
    }
    community = newCommunity
    if (community) {
      if (community.community_type === 'community') {
        threads.set(community.subject_uri, community)
        threadsId.push(community.subject_uri)
      }
    }
    return community
  }

  const addCommunity = async (data: CreateCommunityData): Promise<SwymResponse<ICommunity>> => {
    const community = await service.createCommunity(data)

    community.result.is_hidden = false
    threads.set(community.result.subject_uri, community.result)
    threadsId.push(community.result?.subject_uri)

    return community
  }

  const markAsRead = async (uri: SUBJECT_6W_URI) => {
    const community = getCommunityByUri(uri)
    if (
      community?.last_message?.message_id
      && community.last_message.message_id !== community.mark?.message_id
    ) {
      const tmp = community.mark
      community.mark = community.last_message
      try {
        await service.markAsRead({
          messageId: community.last_message?.message_id,
          communityUri: uri,
        })
      }
      catch {
        community.mark = tmp
      }
    }
  }

  const markAllAsRead = async (context: 'favorite_communities' | 'communities') => {
    const response = await service.markAllAsRead(context)
    globalMarks.set(response.result.context, response.result.message_id)
  }

  const followCommunity = async (id: string) => {
    const community = getCommunityByUri(id) as ICommunity
    if (community.is_hidden === false)
      return
    const response = await service.followCommunity(id)
    if (response.result.success)
      community.is_hidden = false
  }

  const unfollowCommunity = async (id: string) => {
    const community = getCommunityByUri(id) as ICommunity
    const response = await service.unfollowCommunity(id)
    if (response.result.success)
      community.is_hidden = true
  }

  const leaveCommunity = async (uri: string) => {
    await service.leaveCommunity(uri)
    threadsId.splice(threadsId.indexOf(uri), 1)
    threads.delete(uri)
  }
  /**
   * @const highlightContent highlight a content
   * @param content content to highlight
   */
  const highlightContent = async (content: IContent) => {
    const response = await service.highlightContent({
      community_id: content.community_subject_uri,
      highlighted_content_uri: content.subject_uri,
    })
    const community = getCommunityByUri(content.community_subject_uri)
    if (community && response.result)
      community.highlighted_content_uri = content.subject_uri
  }

  const removeHighlightedContent = async (content: IContent) => {
    const response = await service.highlightContent({
      community_id: content.community_subject_uri,
      highlighted_content_uri: '',
    })
    const community = getCommunityByUri(content.community_subject_uri)
    if (community && response.result)
      community.highlighted_content_uri = ''
  }
  // #endregion ACTIONS

  return {
    // #region STATES
    threads,
    threadsId,
    globalMarks,
    // #endregion STATES

    // #region COMPUTED
    countUnread,
    // #endregion COMPUTED

    // #region METHODS
    getCommunityByUri,
    getCommunityById,
    getCommunityUserAccess,
    isCommunityUnread,
    isCommunitySuperUser,
    isCommunityOwner,
    isCommunityAuthor,
    isCommunityContributor,
    isContributorAllowedToCreateContent,
    isBusinessExperienceCommunity,
    hasContentCreationAccess,
    // #endregion METHODS

    // #region ACTIONS
    doFilter,
    fetchList: () => cacheDecorator('fetch-communities', fetchList, 36e5 /* 1hr cache */),
    fetchCommunity: (id: string) => cacheDecorator(
      `community-fetch-${id}`,
      fetchCommunity.bind(null, id),
      5000,
    ), // avoid double fetch on community access (page-access & index)
    markAsRead,
    markAllAsRead,
    addCommunity,
    followCommunity,
    unfollowCommunity,
    leaveCommunity,
    highlightContent,
    removeHighlightedContent,
    getOrFetchCommunity,
    setCommunityExplicitTags,
    // #endregion ACTIONS

    // #region FAVORITES
    favorites: computed(() => storeFavorites.get('community')),
    deleteFavorite: storeFavorites.remove.bind(null, 'community'),
    addFavorite: storeFavorites.add.bind(null, 'community'),
    // #endregion FAVORITES
  }
})
